import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { HistoryPanel } from './HistoryPanel'
import { Facts, Panel, RecordHeader, RecordTabs, type Fact, type HeaderAction } from './record'
import { formatMoment, listPath } from './recordHelpers'
import { FailureAlert, RecordFailure, RecordSkeleton, StatusBadge, failureText } from './recordKit'
import {
  answerTicket,
  assignTicket,
  changeTicketStatus,
  fetchAssignees,
  fetchTicket,
  markTicketUrgent,
  noteTicket,
  type TicketCard,
  type TicketStatus,
} from './supportApi'
import { TriagePanel } from './SupportTriagePanel'
import type { RecordViewProps } from './types'
import { ReplyDraftButton } from '@/ai/ReplyDraft'

/**
 * Карточка обращения клиента — рабочее место менеджера (тикет #123).
 *
 * Переписка и внутренние заметки — одна лента по времени, но заметка выглядит иначе и
 * подписана «видно только сотрудникам»: перепутать, что ушло клиенту, а что нет, — ровно та
 * ошибка, которую экран обязан не допускать. Поле ответа переключается между «Ответ
 * клиенту» и «Внутренняя заметка» явно, а не галочкой сбоку.
 *
 * Действия показываются, только если право `update` вызывающего достаёт до этого обращения
 * (`mayUpdate` с сервера, по той же области, что и проверка): кнопка, ведущая к
 * гарантированному 404, учит игнорировать отказы. Запрет при этом на сервере.
 *
 * Срок первого ответа виден всегда: «просрочено» — красным, уложились или нет — словами
 * после первого ответа.
 */
export function SupportTicketRecordView({ recordId }: RecordViewProps) {
  const { t, locale } = useApp()
  const queryClient = useQueryClient()
  const [tab, setTab] = useState('conversation')
  const [mode, setMode] = useState<'answer' | 'note'>('answer')
  const [draft, setDraft] = useState('')
  const [draftTaskId, setDraftTaskId] = useState<string | null>(null)
  const [failure, setFailure] = useState<string | null>(null)

  const ticket = useQuery({ queryKey: ['support-ticket', recordId], queryFn: ({ signal }) => fetchTicket(recordId, signal), retry: false })
  const mayUpdate = ticket.data?.mayUpdate === true
  const assignees = useQuery({
    queryKey: ['support-assignees'],
    queryFn: ({ signal }) => fetchAssignees(signal),
    enabled: mayUpdate,
    retry: false,
  })

  const settle = (card: TicketCard) => {
    setFailure(null)
    queryClient.setQueryData(['support-ticket', recordId], card)
    void queryClient.invalidateQueries({ queryKey: ['list', 'support.ticket'] })
    void queryClient.invalidateQueries({ queryKey: ['record', 'support.ticket', recordId] })
  }
  const fail = (error: unknown) => setFailure(failureText(error, t('app.error')))

  const send = useMutation({
    mutationFn: () => (mode === 'answer' ? answerTicket(recordId, draft, draftTaskId) : noteTicket(recordId, draft)),
    onSuccess: (card) => {
      setDraft('')
      setDraftTaskId(null)
      settle(card)
    },
    onError: fail,
  })
  const assign = useMutation({ mutationFn: (userId: string | null) => assignTicket(recordId, userId), onSuccess: settle, onError: fail })
  const move = useMutation({ mutationFn: (status: TicketStatus) => changeTicketStatus(recordId, status), onSuccess: settle, onError: fail })
  const mark = useMutation({ mutationFn: (urgent: boolean) => markTicketUrgent(recordId, urgent), onSuccess: settle, onError: fail })

  if (ticket.isLoading) return <RecordSkeleton />
  if (!ticket.data) {
    return <RecordFailure error={ticket.error} onRetry={() => void ticket.refetch()} listPath={listPath('support.ticket', '/support')} />
  }
  const current = ticket.data
  const entries = current.entries ?? []
  const open = current.status === 'open' || current.status === 'answered'
  const busy = send.isPending || assign.isPending || move.isPending || mark.isPending
  const urgent = current.urgent === true

  const transitions: HeaderAction[] = mayUpdate
    ? (current.transitions ?? []).map((status) => ({
        id: `status-${status}`,
        label: t(`support.action.${status}`),
        onClick: () => move.mutate(status),
        disabled: busy,
      }))
    : []
  // A person may undo what a routing rule decided (#132), and mark what no rule caught.
  if (mayUpdate && open) {
    transitions.push({
      id: 'urgent',
      label: urgent ? t('support.urgent.clear') : t('support.urgent.set'),
      onClick: () => mark.mutate(!urgent),
      disabled: busy,
    })
  }

  const sla: Fact = {
    id: 'sla',
    label: t('support.fact.sla'),
    value: (
      <span className={current.slaBreached ? 'danger' : undefined} data-testid="ticket-sla">
        {formatMoment(current.firstResponseDueAt, locale)}
      </span>
    ),
    sub: current.slaBreached
      ? <span className="badge b-danger">{t('support.sla.breached')}</span>
      : current.slaMet == null
        ? t('support.sla.waiting')
        : current.slaMet
          ? t('support.sla.met')
          : t('support.sla.late'),
  }
  const facts: Fact[] = [
    {
      id: 'status',
      label: t('column.status'),
      value: (
        <span className="row" style={{ gap: 6 }}>
          <StatusBadge entity="support.ticket" value={current.status} />
          {urgent && <span className="badge b-danger" data-testid="ticket-urgent">{t('support.urgent')}</span>}
        </span>
      ),
    },
    {
      id: 'client',
      label: t('support.fact.client'),
      value: (
        <Link to={`${listPath('sales.counterparty', '/counterparties')}/${current.counterpartyId}`}>
          {current.counterpartyName ?? current.counterpartyId}
        </Link>
      ),
    },
    { id: 'assignee', label: t('column.assigned_user_id'), value: current.assignedUserName ?? t('support.unassigned') },
    sla,
  ]

  return (
    <div className="rec-page">
      <RecordHeader
        crumbs={[{ label: t('nav.support'), to: listPath('support.ticket', '/support') }, { label: current.number, mono: true }]}
        title={current.subject}
        meta={[
          t(`value.support.ticket.kind.${current.kind}`),
          t(`value.support.ticket.source.${current.source}`),
          formatMoment(current.createdAt, locale),
        ]}
        primary={transitions[0] ?? null}
        secondary={transitions.slice(1)}
      />
      <FailureAlert text={failure} />
      <Facts items={facts} ariaLabel={t('record.facts')} />

      <RecordTabs
        tabs={[
          { id: 'conversation', label: t('support.tab.conversation'), count: entries.length },
          { id: 'history', label: t('record.history.changes') },
        ]}
        active={tab}
        onChange={setTab}
        ariaLabel={t('support.sections')}
      >
        {tab === 'conversation' && (
          <div className="layout-2-wide" style={{ paddingTop: 14 }}>
            <div className="rec-col">
              <Panel title={t('support.tab.conversation')} count={entries.length}>
                <ol className="stack" style={{ gap: 10, listStyle: 'none', padding: 0, margin: 0 }} aria-label={t('support.conversation')}>
                  {entries.map((entry) => (
                    <li
                      key={entry.id}
                      className={entry.type === 'note' ? 'alert alert-warning' : 'panel'}
                      data-entry={entry.type}
                      style={{ padding: 10, marginLeft: entry.side === 'manager' ? 32 : 0, marginRight: entry.side === 'client' ? 32 : 0 }}
                    >
                      <div className="row t-sm muted" style={{ gap: 8 }}>
                        <span className="strong">
                          {entry.type === 'note'
                            ? t('support.entry.note')
                            : entry.side === 'client'
                              ? t('support.entry.client')
                              : t('support.entry.manager')}
                        </span>
                        {entry.authorName && <span>{entry.authorName}</span>}
                        <span className="spacer" />
                        <span>{formatMoment(entry.createdAt, locale)}</span>
                      </div>
                      <p style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap' }}>{entry.body}</p>
                    </li>
                  ))}
                </ol>
              </Panel>
              {mayUpdate && (
                <Panel title={t('support.reply.title')}>
                  <div className="stack" style={{ gap: 8 }}>
                    <div className="row" role="radiogroup" aria-label={t('support.reply.mode')} style={{ gap: 8 }}>
                      {(['answer', 'note'] as const).map((value) => (
                        <label key={value} className="row" style={{ gap: 4 }}>
                          <input type="radio" name="support-mode" value={value} checked={mode === value} onChange={() => setMode(value)} />
                          {t(`support.reply.${value}`)}
                        </label>
                      ))}
                    </div>
                    {mode === 'answer' && !open && <p className="t-sm muted" role="note">{t('support.reply.closed')}</p>}
                    {mode === 'answer' && open && (
                      <ReplyDraftButton channel="support" sourceId={recordId} onDraft={(text, taskId) => {
                        setDraft(text)
                        setDraftTaskId(taskId)
                      }} />
                    )}
                    <label className="stack" style={{ gap: 4 }}>
                      <span className="t-sm">{mode === 'answer' ? t('support.reply.answerHint') : t('support.reply.noteHint')}</span>
                      <textarea
                        className="input"
                        rows={4}
                        value={draft}
                        aria-label={t(`support.reply.${mode}`)}
                        onChange={(event) => setDraft(event.target.value)}
                      />
                    </label>
                    <div className="row">
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={busy || draft.trim() === '' || (mode === 'answer' && !open)}
                        onClick={() => send.mutate()}
                      >
                        {mode === 'answer' ? t('support.reply.send') : t('support.reply.saveNote')}
                      </button>
                    </div>
                  </div>
                </Panel>
              )}
            </div>
            <div className="rec-col">
              <TriagePanel triage={current.triage} kind={current.kind} />
              <Panel title={t('column.assigned_user_id')}>
                {mayUpdate ? (
                  <label className="stack" style={{ gap: 4 }}>
                    <span className="t-sm">{t('support.assign.label')}</span>
                    <select
                      className="input"
                      aria-label={t('support.assign.label')}
                      value={current.assignedUserId ?? ''}
                      disabled={busy || current.status === 'closed'}
                      onChange={(event) => assign.mutate(event.target.value === '' ? null : event.target.value)}
                    >
                      <option value="">{t('support.unassigned')}</option>
                      {current.assignedUserId != null &&
                        !(assignees.data ?? []).some((person) => person.id === current.assignedUserId) && (
                          <option value={current.assignedUserId}>{current.assignedUserName ?? current.assignedUserId}</option>
                        )}
                      {(assignees.data ?? []).map((person) => (
                        <option key={person.id} value={person.id}>{person.name}</option>
                      ))}
                    </select>
                  </label>
                ) : (
                  <p>{current.assignedUserName ?? t('support.unassigned')}</p>
                )}
              </Panel>
              {current.rating != null && (
                <Panel title={t('support.rating')}>
                  <p>{'★'.repeat(current.rating)}{current.ratingComment ? ` — ${current.ratingComment}` : ''}</p>
                </Panel>
              )}
            </div>
          </div>
        )}
        {tab === 'history' && (
          <div style={{ paddingTop: 14 }}>
            <Panel title={t('record.history.changes')}>
              <HistoryPanel entity="support.ticket" recordId={recordId} />
            </Panel>
          </div>
        )}
      </RecordTabs>
    </div>
  )
}
